import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom, SCOPES, useHasScope } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { VehicleEditableParameterVersion } from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * キャリブレーション編集可能候補値取得API
 */
export const useGetCalibrationParameterEditConfigsAPI = (): {
  state: APIState;
  getCalibrationParameterEditConfigs: (
    vehicle_id: string,
    lang: string,
  ) => Promise<VehicleEditableParameterVersion | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const fmsAPI = useFMSAPI();
  const getHasScope = useHasScope();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const getCalibrationParameterEditConfigsAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      lang: string,
    ): Promise<Result<VehicleEditableParameterVersion, AxiosResponse>> => {
      try {
        const res = await fmsAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/editable_calibration_parameter_configs?lang=${lang}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const getCalibrationParameterEditConfigs = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, lang: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.get_editable_vehicle_calibration_parameters', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        if (!getHasScope(SCOPES.DescribeCalibrationParameterEditConfigs)) {
          setState('hasError');
          return null;
        }
        const res = await getCalibrationParameterEditConfigsAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
          lang,
        );
        if (!res || res.isFailure()) {
          notifyError({
            message: `${t(
              'api.fms.get_editable_vehicle_calibration_parameters',
              {
                status: 'failed',
              },
            )}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        setState('hasValue');
        return res.value;
      },
      [
        notifyError,
        getCalibrationParameterEditConfigsAPI,
        getHasScope,
        t,
        getErrorMessage,
      ],
    ),
  );

  return {
    state,
    getCalibrationParameterEditConfigs,
  };
};
